import { ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { DetailsItemComponent } from '@shared/components/details-item/details-item.component';
import { RestrictionEditItemFieldComponent } from '@shared/components/financial-account/edit-restrictions/restriction-edit-item-field/restriction-edit-item-field.component';
import { FinancialAccountRestriction } from '@shared/models';
import { FinancialAccountService, NotificationService } from '@shared/services';
import { ErrorUtils, safeJSONparse } from '@shared/utils';
import { upperFirst } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-edit-restrictions',
  imports: [
    RktIconComponent,
    DetailsItemComponent,
    RktButtonDirective,
    ReactiveFormsModule,
    FormsModule,
    RestrictionEditItemFieldComponent,
  ],
  templateUrl: './edit-restrictions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditRestrictionsComponent implements OnDestroy {
  activeModal = inject(ActiveModal);
  financialAccountService = inject(FinancialAccountService);
  notificationService = inject(NotificationService);

  restrictions: FinancialAccountRestriction[] = [];
  financialAccountId?: string;

  hasError = false;

  isLoading = false;

  formError?: string;

  private destroy$ = new Subject<void>();

  initModalData(restrictions: FinancialAccountRestriction[], financialAccountId: string): void {
    this.financialAccountId = financialAccountId;
    this.restrictions = restrictions;
  }

  onRestrictionChange(restriction: FinancialAccountRestriction, index: number) {
    const newRes = [...this.restrictions];
    newRes[index] = restriction;
    this.restrictions = newRes;
  }

  onRestrictionError(error: boolean) {
    this.hasError = error;
  }

  onSave() {
    this.isLoading = true;

    const newRestrictions = this.restrictions.map((restriction) => ({
      solution: restriction.solution,
      transactionType: restriction.transactionType,
      withholdingPeriod: restriction.withholdingPeriod,
      withholdingPeriodType: restriction.withholdingPeriodType,
    }));

    this.financialAccountService
      .updateFinancialAccountRestrictions({
        financialAccountId: this.financialAccountId!,
        restrictions: newRestrictions,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (financialAccount) => {
          this.notificationService.displaySuccess(`Financial account restrictions updated successfully.`, 'Success');
          this.isLoading = false;
          this.activeModal.close(financialAccount);
        },
        error: (error) => {
          this.isLoading = false;
          this.handleError(error, 'financialAccountService.updateFinancialAccountRestrictions');
        },
      });
  }

  private handleError(error: string | number, serviceInfo: string): void {
    const isErrorMessage = typeof error === 'string' && !safeJSONparse(error);

    this.formError = this.parseError(isErrorMessage ? error : ErrorUtils.parseExtendedError(error));

    this.isLoading = false;
    ErrorUtils.catchError(`${serviceInfo} error`, error);
  }

  private parseError(error: string) {
    const matches = error.matchAll(/message='(?<msg>[^']+)'/gi);

    const messages = Array.from(matches, (match) => {
      const { msg } = match?.groups ?? {};
      const errorMsg = upperFirst(msg);
      return errorMsg.slice(-1) === '.' ? errorMsg : `${errorMsg}.`;
    });

    if (!messages.length) {
      const errorMessage = upperFirst(error);
      return errorMessage.slice(-1) === '.' ? errorMessage : `${errorMessage}.`;
    }

    return messages.join('\n');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
